import axios from "axios";
import { payments } from "lobbie";
import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { FiDownload } from "react-icons/fi";
import { Link } from "react-router-dom";
import { generateInvoiceBlob } from "src/components/shared/printing/PrintInvoiceButton";
import { BASE_API_URL, BASE_PUBLIC_API_ROUTE } from "src/constants";
import { handleError, notify } from "src/utils";

interface PaymentConfirmationNextStepsProps {
    actionIdentifier: string;
}

interface StepProps {
    number: number;
    title: string;
    description: string;
    isCurrentStep?: boolean;
}

const Step: React.FC<StepProps> = ({ number, title, description, isCurrentStep = false }) => (
    <div
        className="position-relative mb-4 p-3 rounded"
        style={{
            backgroundColor: "#ffffff",
            border: isCurrentStep ? "2px solid #900303" : "1px solid #dee2e6",
        }}
    >
        {/* Current step indicator and spacing */}
        {isCurrentStep && (
            <>
                <div
                    className="position-absolute text-white px-3 py-1 rounded fw-bold"
                    style={{
                        top: "-12px",
                        left: "20px",
                        backgroundColor: "#000000",
                    }}
                >
                    Your current step
                </div>
                <div className="mt-3" />
            </>
        )}

        {/* Top row with number and title */}
        <div className="d-flex mb-2 align-items-center">
            <div
                className="flex-shrink-0 me-3 d-flex align-items-center justify-content-center rounded-circle fw-bold"
                style={{
                    width: "30px",
                    height: "30px",
                    color: "#900303",
                    backgroundColor: "#fbe5e6",
                }}
            >
                {number}
            </div>
            <div className="fw-bold mt-2 h5">{title}</div>
        </div>

        {/* Description spanning full width */}
        <div>
            <p
                className="mb-0"
                style={{
                    color: "#333333",
                    fontSize: "16px",
                    lineHeight: "1.5",
                }}
            >
                {description}
            </p>
        </div>
    </div>
);

const PaymentConfirmationNextSteps: React.FC<PaymentConfirmationNextStepsProps> = ({
    actionIdentifier,
}) => {
    const steps = [
        {
            title: "Your Test Kit Is Being Prepped for Shipment",
            description:
                "We're preparing your home testosterone test kit. It will ship within 1 business day and typically arrives in 1-2 days.",
        },
        {
            title: "Use the Kit and Ship It to Our Partner Lab",
            description:
                "Inside your kit, you'll find simple instructions and a prepaid return label. Be sure to send it back promptly for the fastest results.",
        },
        {
            title: "We'll Review Your Results",
            description:
                "Once your sample is processed, your lab results will be securely reviewed by a licensed provider.",
        },
        {
            title: "Receive your results",
            description:
                "We'll send you a secure email notification when your lab results are ready.",
        },
        {
            title: "Schedule Your Telehealth Visit",
            description:
                "After your results are in, we'll send you a secure link to book your virtual consultation to discuss your personalized TRT plan.",
        },
    ];

    const handleDownloadReceipt = async () => {
        try {
            // Fetch receipt data from backend
            const response = await axios.get<payments.print.IInvoicePrintData>(
                `${BASE_API_URL}/${BASE_PUBLIC_API_ROUTE}/remote-care/print-receipt-data/${actionIdentifier}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                },
            );
            const receiptData = response.data;
            // Generate PDF blob using the new function
            const blob = await generateInvoiceBlob(receiptData);
            if (blob) {
                // Create URL and open in new tab
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank");
                // Clean up the URL after a short delay
                setTimeout(() => {
                    URL.revokeObjectURL(url);
                }, 1000);
            } else {
                handleError(new Error("Failed to generate PDF blob"));
                notify({
                    title: "Failed to download receipt",
                    level: "error",
                });
            }
        } catch (error) {
            handleError(error as Error);
            notify({
                title: "Failed to download receipt",
                level: "error",
            });
        }
    };

    return (
        <div
            className="w-100 d-flex align-items-start justify-content-center"
            style={{ backgroundColor: "#fffcf8" }}
        >
            <Container className="py-3 py-lg-5">
                <Row className="justify-content-center">
                    <Col className="px-3 px-lg-0" style={{ maxWidth: "600px" }}>
                        <div className="text-left mb-4">
                            <div className="mb-2 d-lg-none" />
                            <div className="fw-bold text-dark mb-0 fs-2 fs-lg-1">
                                You're all set!
                            </div>
                            <div className="mt-2 mt-lg-3" />
                            <p className="text-muted">
                                Your health journey starts today - your at-home lab kit is on its
                                way.
                            </p>
                            <div className="mt-3" />
                            {/* Download Receipt Link */}
                            <Link
                                onClick={handleDownloadReceipt}
                                className="d-flex align-items-center text-decoration-underline d-lg-inline-flex"
                                style={{ color: "#900303" }}
                                to={undefined}
                            >
                                <FiDownload className="me-2" size={16} />
                                Download Receipt
                            </Link>
                        </div>
                        <div className="mb-4 mb-lg-5">
                            <div className="fw-bold text-dark mb-4 h3">What's Next:</div>
                            {steps.map((step, index) => (
                                <Step
                                    key={index}
                                    number={index + 1}
                                    title={step.title}
                                    description={step.description}
                                    isCurrentStep={index === 0}
                                />
                            ))}
                        </div>
                        {/* Need to Check on Your Order section */}
                        <div className="mb-4">
                            <div className="fw-bold text-dark mb-3 h3">
                                Need to Check on Your Order?
                            </div>
                            <p className="text-muted mb-0">
                                You'll receive an email with tracking info as soon as your kit
                                ships. If you need support, email us at{" "}
                                <a
                                    href="mailto:support@gamedaymenshealth.com"
                                    style={{ color: "#900303" }}
                                >
                                    support@gamedaymenshealth.com
                                </a>
                            </p>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default PaymentConfirmationNextSteps;
